"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

interface PlaidAccount {
  id: string;
  plaid_account_id: string;
  account_id: string;
  name: string;
  official_name?: string | null;
  institution_name?: string | null;
  subtype?: string | null;
  type?: string | null;
  mask?: string | null;
  current_balance?: number | null;
  available_balance?: number | null;
  currency?: string | null;
}

interface PlaidAccountsContextValue {
  accounts: PlaidAccount[];
  loading: boolean;
  error: string | null;
  hasAccounts: boolean;
  refetch: () => Promise<void>;
  clearAccounts: () => void;
  reauthDetails: {
    itemId: string;
    plaidItemId: string;
    institutionName: string | null;
    errorCode: string | null;
  } | null;
  lastFetched: Date | null;
}

const PlaidAccountsContext = createContext<
  PlaidAccountsContextValue | undefined
>(undefined);

export interface PlaidAccountsProviderProps {
  children: ReactNode;
  initialAccounts?: PlaidAccount[];
}

export function PlaidAccountsProvider({
  children,
  initialAccounts,
}: PlaidAccountsProviderProps) {
  // No fabricated fallback accounts: an empty list until real Plaid data is
  // wired up. Demo data, when present, comes from the backend like real data.
  const [accounts, setAccounts] = useState<PlaidAccount[]>(() =>
    initialAccounts && initialAccounts.length > 0 ? initialAccounts : [],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(new Date());

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // In production this would call TRPC; for now we simulate a quick refresh.
      await new Promise((resolve) => setTimeout(resolve, 100));
      setLastFetched(new Date());
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Unable to refresh accounts",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const clearAccounts = useCallback(() => {
    setAccounts([]);
  }, []);

  const hasAccounts = accounts.length > 0;

  const value = useMemo<PlaidAccountsContextValue>(
    () => ({
      accounts,
      loading,
      error,
      hasAccounts,
      refetch,
      clearAccounts,
      reauthDetails: null,
      lastFetched,
    }),
    [
      accounts,
      loading,
      error,
      hasAccounts,
      refetch,
      clearAccounts,
      lastFetched,
    ],
  );

  return (
    <PlaidAccountsContext.Provider value={value}>
      {children}
    </PlaidAccountsContext.Provider>
  );
}

export function usePlaidAccountsContext(): PlaidAccountsContextValue {
  const context = useContext(PlaidAccountsContext);
  if (!context) {
    throw new Error(
      "usePlaidAccountsContext must be used within a PlaidAccountsProvider",
    );
  }
  return context;
}
